import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { authedV3Request } from "utils/api/requests";
import { selectAuthToken } from "containers/App/selectors";

/*
 * Create a user (super_admin only). POST /api/v3/admin/users with a `user`
 * object — the controller permits { first_name, last_name, email, password,
 * company, permission_role } (verified against
 * Api::V3::Admin::UsersController#create + its user_params; note it does NOT
 * permit skip_confirmation — Devise :confirmable is disabled on User, so there
 * is no confirmation step to skip. Never send it). The 201 body is the
 * ADMIN_GET_USER_SHAPE detail object.
 *
 * On success we invalidate the cached user list so the new row appears without
 * a manual refresh. The backend still enforces super_admin (403) regardless of
 * the client guard.
 */
export const useCreateAdminUser = () => {
  const authToken = useSelector(selectAuthToken());
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user) => {
      if (!authToken) throw new Error("No auth token available");
      return authedV3Request("v3/admin/users", authToken, {
        method: "POST",
        body: JSON.stringify({ user }),
      });
    },
    mutationKey: ["admin", "createUser"],
    onSuccess: () => {
      // Drop every cached admin user list so the next render refetches with the
      // new user included. Scoped to the list key prefix, not the detail caches.
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

/*
 * Update a user (super_admin only). PATCH /api/v3/admin/users/:id with a `user`
 * object. Verified against Api::V3::Admin::UsersController#update +
 * UserPolicy#permitted_attributes_for_update: a super_admin may set
 * { first_name, last_name, email, password, company, permission_role }
 * (permission_role is gated to super_admin server-side — the policy strips it
 * otherwise, closing AUD-006). Returns 200 with the updated ADMIN_GET_USER_SHAPE.
 * Invalidates both the detail cache for this id and the list.
 */
export const useUpdateAdminUser = (id) => {
  const authToken = useSelector(selectAuthToken());
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user) => {
      if (!authToken) throw new Error("No auth token available");
      return authedV3Request(`v3/admin/users/${id}`, authToken, {
        method: "PATCH",
        body: JSON.stringify({ user }),
      });
    },
    mutationKey: ["admin", "updateUser", id],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "user", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

/*
 * Delete a user (super_admin only). DELETE /api/v3/admin/users/:id. Verified
 * against Api::V3::Admin::UsersController#destroy — returns 204 No Content (no
 * body; the request util resolves to {} on 204). Invalidates the list; removes
 * the now-stale detail cache for this id.
 */
export const useDeleteAdminUser = (id) => {
  const authToken = useSelector(selectAuthToken());
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!authToken) throw new Error("No auth token available");
      return authedV3Request(`v3/admin/users/${id}`, authToken, {
        method: "DELETE",
      });
    },
    mutationKey: ["admin", "deleteUser", id],
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["admin", "user", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

/*
 * Assign the unlimited plan to a user (super_admin only). Revenue-adjacent:
 * POST /api/v3/admin/users/:id/assign_unlimited_plan with no body. Verified
 * against Api::V3::Admin::UsersController#assign_unlimited_plan — server-side it
 * clears the user's Stripe customer state, sets the memento unlimited annual
 * plan, and resets the subscription window (transactional). Returns 200 with the
 * updated ADMIN_GET_USER_SHAPE. Invalidates the detail + list.
 */
export const useAssignUnlimitedPlan = (id) => {
  const authToken = useSelector(selectAuthToken());
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!authToken) throw new Error("No auth token available");
      return authedV3Request(`v3/admin/users/${id}/assign_unlimited_plan`, authToken, {
        method: "POST",
      });
    },
    mutationKey: ["admin", "assignUnlimitedPlan", id],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "user", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

/*
 * Write the global terms text (super_admin only). PUT /api/v3/admin/settings
 * with a `setting` object — the controller permits only { camber_terms } and
 * returns 200 { camber_terms } (verified against Api::V3::Admin::SettingsController
 * #update; Setting is a singleton, so there's no id in the path). Invalidates
 * the cached terms read so the editor + public terms page reflect the new copy.
 */
export const useUpdateTerms = () => {
  const authToken = useSelector(selectAuthToken());
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (camber_terms) => {
      if (!authToken) throw new Error("No auth token available");
      return authedV3Request("v3/admin/settings", authToken, {
        method: "PUT",
        body: JSON.stringify({ setting: { camber_terms } }),
      });
    },
    mutationKey: ["admin", "updateTerms"],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "terms"] });
    },
  });
};
